import React, { createContext, type ReactNode, useContext } from 'react';

// @internal
enum SizeTypeEnum {
    default = 'default', // default behavior can vary depending on the component
    large = 'large',
    medium = 'medium',
    small = 'small'
}

export type SizeType = keyof typeof SizeTypeEnum;
export const getIsSizeType = (value: unknown): value is SizeType => {
    return typeof value === 'string' && Object.keys(SizeTypeEnum).includes(value);
};

export const SEMANTIC_SIZES = Object.keys(SizeTypeEnum).filter(
    (key) => key !== SizeTypeEnum.default
);

const SizeContext = createContext<SizeType | undefined>(undefined);

export interface SizeContextProps {
    size?: SizeType;
    children?: ReactNode;
}

export const SizeContextProvider = ({ children, size }: SizeContextProps) => {
    const originSize = useContext<SizeType | undefined>(SizeContext);
    return <SizeContext.Provider value={size || originSize}>{children}</SizeContext.Provider>;
};

type DynamicSizeType<T> = T extends null ? SizeType | null : SizeType;

export const useSize = <T extends SizeType | null>(
    sizeProp: SizeType | undefined,
    defaultSize: T,
    compName: string
): DynamicSizeType<T> => {
    const configSize = useContext(SizeContext);
    const size = ((sizeProp ?? configSize) || defaultSize) as SizeType | null;
    const isSizeType = getIsSizeType(size);
    const isDev = process.env.NODE_ENV !== 'production';

    if (size !== null && !isSizeType && isDev) {
        throw new Error(
            `Invalid size value for ${compName}. Expected one of: ${Object.values(SizeTypeEnum).join(', ')}. Received: ${size}`
        );
    }

    if (size === 'default') {
        return defaultSize as DynamicSizeType<T>;
    }

    return size as DynamicSizeType<T>;
};

export default SizeContext;
